import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  IconButton,
  Pagination,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatActionType } from './NotificationBell';
import { useState, useMemo, useEffect } from 'react';
import { Icon as IconifyIcon } from '@iconify/react';
import clipboardCheckMultipleOutline from '@iconify/icons-mdi/clipboard-check-multiple-outline';
import { formatRelativeTime } from 'src/utils/relativeTime';
import { isWatchedNotification } from 'src/utils/notificationFields';

// Helper function to format dates (same as in MSForm)
function formatDateDMY(dateStr) {
  if (!dateStr) {
    return '-';
  }
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

// Helper function to get user-friendly bulk notification messages
const getBulkMessage = (
  actionType,
  count,
  isWatched = false,
  notification = null
) => {
  // Get source from notification data
  const getSourceFromData = (notification) => {
    if (!notification) {
      return null;
    }
    const sourceType = notification.related_objects?.source_type;
    if (sourceType === 'rent_roll_file') {
      return 'rent roll upload';
    }
    if (sourceType === 'bulk_communication') {
      return 'bulk communications';
    }
    return null;
  };

  const source = getSourceFromData(notification);

  const actionMessages = {
    bulk_file_owner_assignment: isWatched
      ? source
        ? `File owner changed for ${count} certification${
            count > 1 ? 's' : ''
          } you're watching from ${source}`
        : `File owner changed for ${count} certification${
            count > 1 ? 's' : ''
          } you're watching`
      : source
      ? `You were assigned ${count} certification${
          count > 1 ? 's' : ''
        } as file owner from ${source}`
      : `You were assigned ${count} certification${
          count > 1 ? 's' : ''
        } as file owner`,
    bulk_status_change: `${count} certification${
      count > 1 ? 's' : ''
    } had status changes`,
    bulk_property_transfer: `${count} certification${
      count > 1 ? 's' : ''
    } were transferred`,
    rent_roll_upload: isWatched
      ? `File owner changed for ${count} certification${
          count > 1 ? 's' : ''
        } you're watching from rent roll upload`
      : `You were assigned ${count} certification${
          count > 1 ? 's' : ''
        } as file owner from rent roll upload`,
    bulk_communication: isWatched
      ? `File owner changed for ${count} certification${
          count > 1 ? 's' : ''
        } you're watching from bulk communications`
      : `You were assigned ${count} certification${
          count > 1 ? 's' : ''
        } as file owner from bulk communications`,
    // Add more specific messages as needed
  };

  return (
    actionMessages[actionType] ||
    `${formatActionType(actionType)} - ${count} certification${
      count > 1 ? 's' : ''
    }`
  );
};

export default function BulkNotificationDetails({
  notification,
  open,
  onClose,
}) {
  // Pagination state - must be called before any early returns
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Get certifications from the notification data - must be called before any early returns
  const certifications = useMemo(() => {
    return notification?.certifications || [];
  }, [notification?.certifications]);

  // Calculate pagination - must be called before any early returns
  const totalPages = Math.ceil(certifications.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedCertifications = useMemo(() => {
    return certifications.slice(startIndex, endIndex);
  }, [certifications, startIndex, endIndex]);

  // Reset page when modal opens with a different notification - must be called before any early returns
  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, notification?.id]);

  if (!notification || !notification.is_bulk_notification) {
    return null;
  }

  const { related_objects, content, created, is_read, read_at } = notification;
  const { total_count, action_type } = related_objects || {};

  // Reset page when modal opens/closes or certifications change
  const handleClose = () => {
    setPage(1);
    onClose();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconifyIcon
            icon={clipboardCheckMultipleOutline}
            sx={{ fontSize: 'small' }}
            color="warning"
          />
          <Typography variant="h6" component="div">
            Bulk Certification Action Details
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Notification Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
            {getBulkMessage(
              action_type,
              total_count,
              isWatchedNotification(notification),
              notification
            )}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {content}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Tooltip title={formatRelativeTime(created).tooltipText} arrow>
              <Chip
                label={`Created: ${formatRelativeTime(created).displayText}`}
                variant="outlined"
                size="small"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
            {is_read && read_at && (
              <Tooltip title={formatRelativeTime(read_at).tooltipText} arrow>
                <Chip
                  label={`Read: ${formatRelativeTime(read_at).displayText}`}
                  variant="outlined"
                  size="small"
                  color="success"
                  sx={{ cursor: 'help' }}
                />
              </Tooltip>
            )}
            <Chip
              label={`Total Affected: ${total_count}`}
              variant="outlined"
              size="small"
              color="primary"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Certifications Table */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Affected Certifications ({certifications.length})
            {totalPages > 1 && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: 'normal' }}
              >
                (Page {page} of {totalPages})
              </Typography>
            )}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontStyle: 'italic' }}
          >
            Click anywhere in a row to view the certification details
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    Household
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    Effective Date
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    Deadline
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    Certification Config
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    Property
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCertifications.length > 0 ? (
                  paginatedCertifications.map((cert) => (
                    <TableRow
                      key={cert.id}
                      sx={{
                        py: 0,
                        height: 32,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => {
                        const url = `/workbench/${
                          cert.property?.slug ||
                          cert.property?.name
                            ?.toLowerCase()
                            .replace(/\s+/g, '-')
                        }/${cert.id}/summary`;
                        window.open(url, '_blank');
                      }}
                    >
                      <TableCell
                        sx={{ py: 0, px: 1, fontSize: 13, height: 32 }}
                      >
                        {cert.household?.name || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ py: 0, px: 1, fontSize: 13, height: 32 }}
                      >
                        {formatDateDMY(cert.effective_date)}
                      </TableCell>
                      <TableCell
                        sx={{ py: 0, px: 1, fontSize: 13, height: 32 }}
                      >
                        {cert.deadline ? formatDateDMY(cert.deadline) : '-'}
                      </TableCell>
                      <TableCell
                        sx={{ py: 0, px: 1, fontSize: 13, height: 32 }}
                      >
                        {cert.certification_config?.name || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ py: 0, px: 1, fontSize: 13, height: 32 }}
                      >
                        {cert.property?.name || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No certification details available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
